import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TableBody, TableCell, TableHead, TableRow, Button } from '@mui/material';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import SortingColumn from '#component/table/sorting-column';
import { changeItemsPerPage, changeOrderAndOrderBy } from '#action/addressbook/addressbook-contacts-paginated';
import PreloaderComponent from '#component/preloader';
import NotFoundMessage from '#component/general/not-found-message';
import ContactTableRow from './contact-table-row';
import { StyledTable } from './styles';

const DEFAULT_ITEMS_PER_PAGE = 50;

/**
 * Renders table with contacts.
 * Dispatches sorting actions.
 * @returns {React.FC}
 */

const TableView = () => {
  const dispatch = useDispatch();
  const {
    contacts,
    status,
    order,
    orderBy,
    itemsPerPage
  } = useSelector(s => s.addressbookContactsPaginated, shallowEqual);
  const { addressbook: addressbooksFilter } = useSelector(s => s.listFilter, shallowEqual);
  const filter = addressbooksFilter?.search?.value;
  const idle = status === 'idle';
  const loading = status === 'loading';
  const isEmptyContacts = status === 'succeeded' && !contacts.length && !filter;

  useEffect(() => {
    if (itemsPerPage !== DEFAULT_ITEMS_PER_PAGE) {
      dispatch(changeItemsPerPage(DEFAULT_ITEMS_PER_PAGE));
    }
  }, [dispatch, itemsPerPage]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    dispatch(changeOrderAndOrderBy(newOrder, property));
  };

  if ((idle || loading) && !contacts.length) {
    return <PreloaderComponent />;
  }

  if (filter?.length && !contacts.length) {
    return (
      <NotFoundMessage>
        Contacts not found
      </NotFoundMessage>
    );
  }

  if (isEmptyContacts) {
    return (
      <NotFoundMessage>
        <Button
          variant="contained"
          color="secondary"
          aria-label="Create contact"
          component={Link}
          to="/addressbook/contact/create"
        >
          CREATE CONTACT
        </Button>
      </NotFoundMessage>
    );
  }

  return (
    <StyledTable loading={loading}>
      <TableHead>
        <TableRow>
          <SortingColumn
            id="addressbook"
            title="Address book"
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
          />
          <SortingColumn
            id="firstName"
            title="First name"
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
          />
          <SortingColumn
            id="lastName"
            title="Last name"
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
          />
          <TableCell align="left">
            Company
          </TableCell>
          <TableCell align="left">
            Address
          </TableCell>
          <TableCell align="left" width={80} />
        </TableRow>
      </TableHead>
      <TableBody>
        {contacts.map((contact, index, self) => (
          <ContactTableRow
            key={contact.id}
            contact={contact}
            index={index}
            contacts={self}
            filter={filter}
          />
        ))}
      </TableBody>
    </StyledTable>
  );
};

export default TableView;
